import { Component, h } from 'preact'
import Header from '../header'
import axios from 'axios'
import { DashboardTimezoneDisclaimer } from './DashboardTimezoneDisclaimer'
import { KeyInsightCard } from './KeyInsightCard'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import {FilterOptionDate, FilterOptionItem, FilterOptionRole, MetricItem, StoredState} from './interfaces'
import {
  createDefaultMetricItem,
  getDefaultDateRange,
  fetchContentTypes,
  previousPeriodDifference,
  trackDashboardUsage,
  BRAND_BLUE,
  BRAND_BORDER_GREY,
  BRAND_YELLOW,
  getDefaultSelectedDate,
  normalizeCategories,
  displayLegendsWithMarginBottom,
  updateDateRangeInSessionStorage,
  getStartAndEndDate, dateRangeForDisplay,
  normalizeRoles, getSelectedRolesForRequest
} from './utils'
import { Bar, HorizontalBar } from 'react-chartjs-2'
import {ChartData, ChartOptions, ChartTooltipItem} from 'chart.js'
import 'chartjs-plugin-datalabels'
import PopularTopicsTable from './PopularTopicsTable'
import PopularCategoriesTable from './PopularCategoriesTable'
import Tabs from '../Tabs'
import DashboardSimpleFilter from './DashboardSimpleFilter'
import DashboardDateRangeFilter from './DashboardDateRangeFilter'
import { Loader } from './Loader'
import DashboardCategoryFilter from './DashboardCategoryFilter'
import QueryParameters from './QueryParameters'
import { GraphTitle } from './GraphTitle'
import {subscribe} from '../../utils/globals'

displayLegendsWithMarginBottom()

interface Props {
  categories: any[]
}

interface HorizontalBarChartItem {
  type: string,
  percent: number
}

interface State {
  postsCreated: MetricItem,
  topicsCreated: MetricItem,
  repliesCreated: MetricItem,
  likesGiven: MetricItem,
  positiveContentHelpfulness: MetricItem,
  topicsCreatedPerContentType: Array<HorizontalBarChartItem>,
  topicsPerUserRole: Array<HorizontalBarChartItem>,
  userRoles: Array<FilterOptionRole>,
  selectedRoles: Array<string>,
  mainRoles: Array<string>,
  customRoles: Array<string>,
  isUserRoleDropdownOpen: boolean,
  contentTypes: Array<FilterOptionItem>,
  selectedContentTypes: Array<string>,
  isContentTypeDropdownOpen: boolean,
  selectedPresetDateRangeId: string,
  startDate: Date,
  endDate: Date,
  selectedDate: FilterOptionDate,
  presetDateRangeList: Array<FilterOptionDate>,
  isDateRangeOpen: boolean,
  isLoading: boolean,
  storedState: StoredState,
  postsOverTime: Array<Data>,
  isContentKeyInsightLoading: boolean,
  isPostsCreatedAndLikesGivenGraphLoading: boolean,
  topicsCreatedPerContentTypeGraphLoading: boolean,
  topicsCreatedPerUserRoleGraphLoading: boolean,
  selectedCategories: Array<string>,
  isCategoryDropdownOpen: boolean,
  isSidebarMenuOpen: boolean,
  shouldChartRedraw: boolean
}

interface Data {
  topics: number,
  replies: number,
  likes: number,
  date: string
}

class ContentDashboard extends Component<Props, State> {
  wrapperRef: null | HTMLElement = null
  barChart: null | HTMLElement = null
  horizontalBarChart: null | HTMLElement = null
  resizeTimeout: number | undefined
  constructor () {
    super()
    let { initialStartDate, initialEndDate } = getStartAndEndDate()
    const defaultMetricItem = createDefaultMetricItem()
    const queryParams = new QueryParameters()
    this.setState({
      postsCreated: defaultMetricItem,
      topicsCreated: defaultMetricItem,
      repliesCreated: defaultMetricItem,
      likesGiven: defaultMetricItem,
      positiveContentHelpfulness: defaultMetricItem,
      topicsCreatedPerContentType: [],
      topicsPerUserRole: [],
      userRoles: [],
      selectedRoles: queryParams.has('roles') ? queryParams.get('roles').split(',') : [],
      mainRoles: [],
      customRoles: [],
      isUserRoleDropdownOpen: false,
      contentTypes: [],
      selectedContentTypes: queryParams.has('content_types') ? queryParams.get('content_types').split(',') : [],
      isContentTypeDropdownOpen: false,
      selectedPresetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
      storedState: {
        presetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
        selectedDateRange: getDefaultSelectedDate()
      },
      startDate: queryParams.has('date_from') ? new Date(queryParams.get('date_from')) : initialStartDate,
      endDate: queryParams.has('date_till') ? new Date(queryParams.get('date_till')) : initialEndDate,
      presetDateRangeList: [],
      isDateRangeOpen: false,
      isLoading: false,
      postsOverTime: [],
      isContentKeyInsightLoading: false,
      isPostsCreatedAndLikesGivenGraphLoading: false,
      topicsCreatedPerContentTypeGraphLoading: false,
      topicsCreatedPerUserRoleGraphLoading: false,
      selectedCategories: queryParams.has('categories') ? queryParams.get('categories').split(',') : [],
      isCategoryDropdownOpen: false,
      shouldChartRedraw: false,
      isSidebarMenuOpen: true
    })
    this.toggleUserRoleDropdown = this.toggleUserRoleDropdown.bind(this)
    this.onUserRolesChanged = this.onUserRolesChanged.bind(this)
    this.onResetUserRoles = this.onResetUserRoles.bind(this)
    this.onClearUserRoles = this.onClearUserRoles.bind(this)

    this.toggleContentTypeDropdown = this.toggleContentTypeDropdown.bind(this)
    this.onContentTypesChanged = this.onContentTypesChanged.bind(this)
    this.onResetContentTypes = this.onResetContentTypes.bind(this)
    this.onClearContentTypes = this.onClearContentTypes.bind(this)

    this.toggleDateRange = this.toggleDateRange.bind(this)
    this.onDateRangeChanged = this.onDateRangeChanged.bind(this)
    this.onResetDateRange = this.onResetDateRange.bind(this)
    this.onPresetDateRangeChanged = this.onPresetDateRangeChanged.bind(this)
    this.onCustomDateRangeChanged = this.onCustomDateRangeChanged.bind(this)

    this.toggleCategoryDropdown = this.toggleCategoryDropdown.bind(this)
    this.onResetCategories = this.onResetCategories.bind(this)
    this.onCategoriesChanged = this.onCategoriesChanged.bind(this)
    this.onClearCategories = this.onClearCategories.bind(this)
    this.fetchData = this.fetchData.bind(this)
  }

  async componentDidMount () {
    trackDashboardUsage('content')
    subscribe('control_sidebar_state', (payload) => this.setState({ isSidebarMenuOpen: payload.isOpen }))

    this.redrawGraphsOnResize()
    this.setState({
      isContentKeyInsightLoading: true,
      isPostsCreatedAndLikesGivenGraphLoading: true,
      topicsCreatedPerContentTypeGraphLoading: true,
      topicsCreatedPerUserRoleGraphLoading: true,
      isLoading: true
    })
    try {
      const [presetDateRanges, roles, contentTypes] = await Promise.all([this.fetchDateRangeFilter(), this.fetchUserRoles(), fetchContentTypes()])
      let defaultDateRange = getDefaultDateRange(presetDateRanges, this.state.selectedPresetDateRangeId)
      let { dateFrom, dateTill, selectedDateRange, selectedPresetId , updatedState } = dateRangeForDisplay(this.state.storedState, defaultDateRange, this.state.selectedPresetDateRangeId)
      const { selectedRoles } = this.state
      const mainRoles = getSelectedRolesForRequest(roles, selectedRoles)
      const customRoles = getSelectedRolesForRequest(roles, selectedRoles, false)
      this.setState({
        isLoading: false,
        userRoles: roles,
        contentTypes: contentTypes,
        presetDateRangeList: presetDateRanges,
        selectedPresetDateRangeId: selectedPresetId,
        selectedDate: selectedDateRange,
        startDate: dateFrom,
        endDate: dateTill,
        storedState: updatedState,
        mainRoles: mainRoles,
        customRoles: customRoles
      })
    } catch {
      this.setState({ isLoading: false })
    }
      this.fetchContentKeyInsights()
      this.fetchTopicsRepliesAndLikesOverTime()
      this.fetchTopicsPerContentType()
      this.fetchTopicsPerUserRole()
  }

  addHandler () {
    const { isDateRangeOpen, isUserRoleDropdownOpen, isContentTypeDropdownOpen, isCategoryDropdownOpen } = this.state
    if(isDateRangeOpen || isUserRoleDropdownOpen || isContentTypeDropdownOpen || isCategoryDropdownOpen ) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.onResetDateRange()
      this.setState({
        isUserRoleDropdownOpen: false,
        isContentTypeDropdownOpen: false,
        isCategoryDropdownOpen: false
      }, this.removeHandler)
    }
  }

  async fetchUserRoles () {
    const res = await axios.get('/role/fetchRolesForFilter')
    return normalizeRoles(res.data)
  }

  redrawGraphsOnResize ()  {
    if (typeof window.ResizeObserver === "undefined") {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimeout)
        this.setState({ shouldChartRedraw: true })
        this.resizeTimeout = window.setTimeout(() => this.setState({shouldChartRedraw: false}), 2*10)
    })

    if(this.barChart != null && this.horizontalBarChart != null) {
      resizeObserver.observe(this.barChart);
      resizeObserver.observe(this.horizontalBarChart)
    }
  }

  async refetchData () {
    this.fetchContentKeyInsights()
    this.fetchTopicsRepliesAndLikesOverTime()
    this.fetchTopicsPerContentType()
    this.fetchTopicsPerUserRole()
  }

  async fetchDateRangeFilter () {
    const res = await axios.get('/analyticsV2/fetchDateRangeFilter')
    return res.data.ranges
  }

  async fetchContentKeyInsights () {
    this.setState({isContentKeyInsightLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/contentKeyInsights')
      const {posts, topics, replies, likes, positiveContentHelpfulness} = res.data
      const postsCreatedPercentageDifference = previousPeriodDifference(posts.count, posts.previousCount)
      const topicsCreatedPercentageDifference = previousPeriodDifference(topics.count, topics.previousCount)
      const repliesCreatedPercentageDifference = previousPeriodDifference(replies.count, replies.previousCount)
      const likesGivenPercentageDifference = previousPeriodDifference(likes.count, likes.previousCount)
      const positiveContentHelpfulnessPercentageDifference = previousPeriodDifference(positiveContentHelpfulness.count, positiveContentHelpfulness.previousCount)
      this.setState({
        postsCreated: {
          count: posts.count,
          previousCount: posts.previousCount,
          percentageDifference: postsCreatedPercentageDifference
        },
        topicsCreated: {
          count: topics.count,
          previousCount: topics.previousCount,
          percentageDifference: topicsCreatedPercentageDifference
        },
        repliesCreated: {
          count: replies.count,
          previousCount: replies.previousCount,
          percentageDifference: repliesCreatedPercentageDifference
        },
        likesGiven: {
          count: likes.count,
          previousCount: likes.previousCount,
          percentageDifference: likesGivenPercentageDifference
        },
        positiveContentHelpfulness: {
          count: positiveContentHelpfulness.count,
          previousCount: positiveContentHelpfulness.previousCount,
          percentageDifference: positiveContentHelpfulnessPercentageDifference
        },
        isContentKeyInsightLoading: false
      })
    } catch {
      this.setState({isContentKeyInsightLoading: false})
    }
  }

  async fetchTopicsRepliesAndLikesOverTime () {
    this.setState({isPostsCreatedAndLikesGivenGraphLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/topicsRepliesAndLikesOverTime')
      this.setState({
        postsOverTime: res.data,
        isPostsCreatedAndLikesGivenGraphLoading: false
      })
    } catch {
      this.setState({isPostsCreatedAndLikesGivenGraphLoading: false})
    }
  }

  async fetchTopicsPerContentType () {
    this.setState({topicsCreatedPerContentTypeGraphLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/topicsPerContentType')
      this.setState({
        topicsCreatedPerContentType: res.data,
        topicsCreatedPerContentTypeGraphLoading: false
      })
    } catch {
      this.setState({topicsCreatedPerContentTypeGraphLoading: false})
    }
  }

  async fetchTopicsPerUserRole () {
    this.setState({topicsCreatedPerUserRoleGraphLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/topicsPerUserRole')
      this.setState({
        topicsPerUserRole: res.data,
        topicsCreatedPerUserRoleGraphLoading: false
      })
    } catch {
      this.setState({topicsCreatedPerUserRoleGraphLoading: false})
    }
  }

  async fetchData (url: string) {
    const { startDate, endDate, mainRoles, customRoles, selectedContentTypes, selectedCategories } = this.state
    const filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false),
      mainRoles: mainRoles,
      customRoles: customRoles,
      contentTypes: selectedContentTypes,
      categories: selectedCategories
    }
    return axios.post(url, {
      filters: filterOptions
    })
  }

  toggleUserRoleDropdown (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isContentTypeDropdownOpen) {
      this.onResetContentTypes()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }

    this.setState({isUserRoleDropdownOpen: value}, this.addHandler)
  }

  onUserRolesChanged (data: Array<string>) {
    const { userRoles } = this.state
    const mainRoles = getSelectedRolesForRequest(userRoles, data)
    const customRoles = getSelectedRolesForRequest(userRoles, data, false)
    this.setState({
      isUserRoleDropdownOpen: false,
      selectedRoles: data,
      mainRoles: mainRoles,
      customRoles: customRoles
    })
    window.history.pushState(null, 'roles changed', (new QueryParameters()).set('roles', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetUserRoles () {
    this.setState({
      isUserRoleDropdownOpen: false
    })
  }

  onClearUserRoles (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedRoles: [],
      mainRoles: [],
      customRoles: [],
      isUserRoleDropdownOpen: false
    })
    window.history.pushState(null, 'roles cleared', (new QueryParameters()).unset('roles').getQueryString())
    this.refetchData()
  }

  toggleContentTypeDropdown (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }

    this.setState({ isContentTypeDropdownOpen: value }, this.addHandler)
  }

  onContentTypesChanged (data: Array<string>) {
    this.setState({
      isContentTypeDropdownOpen: false,
      selectedContentTypes: data
    })
    window.history.pushState(null, 'content types changed', (new QueryParameters()).set('content_types', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetContentTypes () {
    this.setState({
      isContentTypeDropdownOpen: false
    })
  }

  onClearContentTypes (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedContentTypes: [],
      isContentTypeDropdownOpen: false
    })
    window.history.pushState(null, 'content_types cleared', (new QueryParameters()).unset('content_types').getQueryString())
    this.refetchData()
  }

  toggleDateRange (value: boolean) {
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    if (this.state.isContentTypeDropdownOpen) {
      this.onResetContentTypes()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }
    this.setState({ isDateRangeOpen: value }, this.addHandler)
  }

  onPresetDateRangeChanged (selectedDate: FilterOptionDate) {
    this.setState({
      selectedPresetDateRangeId: selectedDate.id,
      selectedDate: selectedDate
    })
  }

  onCustomDateRangeChanged (dates: Date[]) {
    const customSelectedDate = {
      id: 'custom_range',
      label: 'Custom Range',
      startDate: dates[0],
      endDate: dates[1]
    }
    this.setState({
      selectedDate: customSelectedDate,
      selectedPresetDateRangeId: customSelectedDate.id
    })
  }

  onDateRangeChanged () {
    const { selectedDate, selectedPresetDateRangeId } = this.state
    let updatedState = Object.assign({}, this.state.storedState)
    updatedState.presetDateRangeId = selectedPresetDateRangeId
    updatedState.selectedDateRange = selectedDate
    this.setState({
      isDateRangeOpen: false,
      startDate: new Date(selectedDate.startDate),
      endDate: new Date(selectedDate.endDate),
      storedState: updatedState
    })
    const queryString = new QueryParameters()
    queryString.set('date_range', selectedPresetDateRangeId)
    if (selectedPresetDateRangeId != 'custom_range') {
      queryString.unset('date_from')
        .unset('date_till')
    } else {
      queryString.set('date_from', AnalyticsDateFormatter.formatDateForUrl(selectedDate.startDate))
        .set('date_till', AnalyticsDateFormatter.formatDateForUrl(selectedDate.endDate))
    }
    window.history.pushState(null, 'date range changed', queryString.getQueryString())
    updateDateRangeInSessionStorage(selectedDate)
    this.refetchData()
  }

  onResetDateRange () {
    const { presetDateRangeId, selectedDateRange } = this.state.storedState
    this.setState({
      isDateRangeOpen: false,
      selectedPresetDateRangeId: presetDateRangeId,
      selectedDate: selectedDateRange
    })
  }

  toggleCategoryDropdown  (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isContentTypeDropdownOpen) {
      this.onResetContentTypes()
    }
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }

    this.setState({ isCategoryDropdownOpen: value }, this.addHandler)
  }

  onCategoriesChanged (data: Array<string>) {
    this.setState({
      selectedCategories: data,
      isCategoryDropdownOpen: false
    })
    window.history.pushState(null, 'categories changed', (new QueryParameters()).set('categories', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetCategories () {
    this.setState({
      isCategoryDropdownOpen: false
    })
  }

  onClearCategories (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedCategories: [],
      isCategoryDropdownOpen: false
    })
    window.history.pushState(null, 'categories cleared', (new QueryParameters()).unset('categories').getQueryString())
    this.refetchData()
  }

  getBarChartOptions (displayLegend: boolean = true): ChartOptions {
    return {
      title: {
        display: false
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 24
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        intersect: false,
        mode: 'index',
        callbacks: {
          title(item: ChartTooltipItem[], data: ChartData): string | string[] {
            if (item.length > 0 && item[0].label) {
              const date = new Date(item[0].label.substr(0, 10))
              const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date)
              const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date)
              return `${month} ${day}`
            }
            return ''
          },
          afterBody(item: Chart.ChartTooltipItem[], data: Chart.ChartData): string | string[] {
            if (item.length > 1) {
              return `Total posts: ${item.reduce((result, curr) => result + parseInt(curr.value || '0'), 0)}`
            }
            return ''
          }
        }
      },
      legend: {
        display: displayLegend,
        position: 'top',
        align: 'start',
        labels: {
          boxWidth: 12,
        }
      },
      scales: {
        xAxes: [{
          type: 'time',
          time: {
            minUnit: 'day'
          },
          gridLines: {
            display: false
          },
          stacked: true,
          offset: true,
          ticks: {
            maxRotation: 0,
            callback: (value, index, values) => {
              return '      ' + value + '      '
            }
          }
        }],
        yAxes: [{
          gridLines: {
            borderDash: [8, 4],
            color: BRAND_BORDER_GREY,
            drawBorder: false
          },
          ticks: {
            padding: 16,
            beginAtZero: true
          },
          stacked: true
        }]
      },
      plugins: {
        datalabels: {
          display: false
        }
      }
    }
  }

  getTopicsCreatedHorizonalBarOptions (): ChartOptions {
    return {
      title: {
        display: false
      },
      layout: {
        padding: {
          left: 0,
          right: 42,
          top: 0,
          bottom: 42
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        enabled: false
      },
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          gridLines: {
            color: 'transparent',
            display: true,
            drawBorder: false
          },
          ticks: {
            display: false,
            beginAtZero: true
          },
        }],
        yAxes: [
          {
            gridLines: {
              display: false
            }
          }
        ]
      },
      plugins: {
        datalabels: {
          align: 'end',
          anchor: 'end',
          borderRadius: 4,
          color: '#6B707E',
          formatter: function (value: string) {
            return value + '%'
          }
        }
      }
    }
  }

  getTopicsCreatedDataSet (data: Array<HorizontalBarChartItem>) {
    return {
      labels: data.map(d => d.type),
      datasets: [
        {
          fill: false,
          data: data.map(d => d.percent),
          borderColor: BRAND_BLUE,
          backgroundColor: BRAND_BLUE,
          barPercentage: 0.5,
          categoryPercentage: 1.0
        }
      ]
    }
  }

  render ({ categories }: Props, { isContentKeyInsightLoading, isPostsCreatedAndLikesGivenGraphLoading, topicsCreatedPerContentTypeGraphLoading, topicsCreatedPerUserRoleGraphLoading, postsCreated, topicsCreated, repliesCreated, likesGiven, positiveContentHelpfulness, topicsCreatedPerContentType, topicsPerUserRole, presetDateRangeList, userRoles, selectedRoles, isUserRoleDropdownOpen, contentTypes, selectedContentTypes, isContentTypeDropdownOpen,  isDateRangeOpen, selectedPresetDateRangeId, startDate, endDate, isLoading, selectedDate, postsOverTime, selectedCategories, isCategoryDropdownOpen, isSidebarMenuOpen, shouldChartRedraw}: State) {
    return (
      <div className={`content-dashboard-overview dashboard-overview ${isSidebarMenuOpen ? 'dashboard-overview-open-menu' : 'dashboard-overview-closed-menu'}`}
           style={{ width: `calc(100vw - ${isSidebarMenuOpen ? '376' : '152'}px)` }}>
        <Header
          title='Content Dashboard'
          disableCancelButton
          disableSaveButton
        />
       <div className='filter-container' ref={node => this.wrapperRef = node}>
          <DashboardDateRangeFilter isLoading={isLoading}
                                    startDate={startDate}
                                    endDate={endDate}
                                    isDropdownOpen={isDateRangeOpen}
                                    items={presetDateRangeList}
                                    selectedDate={selectedDate}
                                    selectedPresetId={selectedPresetDateRangeId}
                                    toggleDropdown={this.toggleDateRange}
                                    onChange={this.onDateRangeChanged}
                                    onReset={this.onResetDateRange}
                                    onCustomDateChange={this.onCustomDateRangeChanged}
                                    onPresetDateChange={this.onPresetDateRangeChanged} />
          <DashboardCategoryFilter isLoading={isLoading}
                                 id='categories'
                                 name='(parent) category or group'
                                 customClassName='categories'
                                 title='Published in'
                                 isDropdownOpen={isCategoryDropdownOpen}
                                 toggleDropdown={this.toggleCategoryDropdown}
                                 resetDropdown={this.onResetCategories}
                                 onClear={this.onClearCategories}
                                 onChange={this.onCategoriesChanged}
                                 items={normalizeCategories(categories)}
                                 selectedItems={selectedCategories} />
          <DashboardSimpleFilter isLoading={isLoading}
                                 id='userRole'
                                 name='user role'
                                 customClassName='user-role'
                                 title='User role'
                                 isDropdownOpen={isUserRoleDropdownOpen}
                                 toggleDropdown={this.toggleUserRoleDropdown}
                                 resetDropdown={this.onResetUserRoles}
                                 onClear={this.onClearUserRoles}
                                 onChange={this.onUserRolesChanged}
                                 items={userRoles}
                                 selectedItems={selectedRoles} />
          <DashboardSimpleFilter isLoading={isLoading}
                                 id='contentType' name='content type'
                                 customClassName='content-type'
                                 title='Content type'
                                 isDropdownOpen={isContentTypeDropdownOpen}
                                 toggleDropdown={this.toggleContentTypeDropdown}
                                 resetDropdown={this.onResetContentTypes}
                                 onClear={this.onClearContentTypes}
                                 onChange={this.onContentTypesChanged}
                                 items={contentTypes}
                                 selectedItems={selectedContentTypes} />
        </div>
        <div className='metric-container'>
          <KeyInsightCard title='Posts created'
                          total={postsCreated.count}
                          titleTooltip='The number of topics and replies created in the selected time period.'
                          percent={postsCreated.percentageDifference}
                          isLoading={isContentKeyInsightLoading}
                          showComparisionUnavailable={postsCreated.previousCount === 0}
                          comparisionTooltip='The percentage difference between the number of topics and replies created in the current/previous time period.' />
          <KeyInsightCard title='Topics created'
                          total={topicsCreated.count}
                          titleTooltip='The number of topics created in the selected time period.'
                          percent={topicsCreated.percentageDifference}
                          isLoading={isContentKeyInsightLoading}
                          showComparisionUnavailable={topicsCreated.previousCount === 0}
                          comparisionTooltip='The percentage difference between the number of topics created in the current/previous time period.' />
          <KeyInsightCard title='Replies created'
                          total={repliesCreated.count}
                          titleTooltip='The number of replies created in the selected time period.'
                          percent={repliesCreated.percentageDifference}
                          showComparisionUnavailable={repliesCreated.previousCount === 0}
                          isLoading={isContentKeyInsightLoading}
                          comparisionTooltip='The percentage difference between the number of replies created in the current/previous time period.' />
          <KeyInsightCard title='Likes given'
                          total={likesGiven.count}
                          titleTooltip='The number of likes given to topics/replies in the selected time period.'
                          percent={likesGiven.percentageDifference}
                          showComparisionUnavailable={likesGiven.previousCount === 0}
                          isLoading={isContentKeyInsightLoading}
                          comparisionTooltip='The percentage difference between the number of likes given in the current/previous time period.' />
          <KeyInsightCard title='Positive content helpfulness votes'
                          total={positiveContentHelpfulness.count}
                          titleTooltip='The number of positive content helpfulness votes given to topics in the selected time period.'
                          isLoading={isContentKeyInsightLoading}
                          percent={positiveContentHelpfulness.percentageDifference}
                          comparisionTooltip='The percentage difference between the number of positive content helpfulness votes given in the current/previous time period.'
                          showComparisionUnavailable={positiveContentHelpfulness.previousCount === 0} />
        </div>
        <div className='graph-wrapper'>
          <div className='graph-container' ref={element => { this.barChart = element }}>
            <div className='graph-row'>
              <GraphTitle customClass='with-legend' title='Posts created'
                  tooltipMessage='The topics and replies created in the selected time period.'
              />
              {isPostsCreatedAndLikesGivenGraphLoading ? <Loader /> : <Bar redraw={shouldChartRedraw} data={{
                datasets: [
                  {
                    label: 'Topics',
                    fill: false,
                    data: postsOverTime.map(d => {return { x: d.date, y: d.topics }}),
                    backgroundColor: BRAND_BLUE
                  },
                  {
                    label: 'Replies',
                    fill: false,
                    data: postsOverTime.map(d => {return { x: d.date, y: d.replies }}),
                    backgroundColor: BRAND_YELLOW
                  }
                ]
              }} options={this.getBarChartOptions(true)} />}
            </div>
            <div className='graph-row'>
              <GraphTitle title='Likes given'
                          tooltipMessage='The likes given to topics/replies in the selected time period.' />
              {isPostsCreatedAndLikesGivenGraphLoading ? <Loader />
              : <Bar redraw={shouldChartRedraw} data={{datasets: [
                  {
                    label: 'Likes given',
                    fill: false,
                    data: postsOverTime.map(d => {return { x: d.date, y: d.likes }}),
                    backgroundColor: BRAND_BLUE
                  }
                ]
              }} options={this.getBarChartOptions(false)} />}
            </div>
          </div>
        </div>
        <div className='graph-wrapper-horizontal-bar'>
          <div className='graph-container-horizontal-bar' ref={element => { this.horizontalBarChart = element }}>
             <div className='graph-row-horizontal-bar'>
               <GraphTitle title='Topics created per content type'
                           customClass='graph-title-horizontal-bar'
                           tooltipMessage='The % of topics created in the selected time period broken down by the content type of the topic (questions/conversations/articles).' />
               {topicsCreatedPerContentTypeGraphLoading ? <Loader />
                   : topicsCreatedPerContentType && <HorizontalBar redraw={shouldChartRedraw} data={this.getTopicsCreatedDataSet(topicsCreatedPerContentType)} options={this.getTopicsCreatedHorizonalBarOptions()} />}
            </div>


            <div className='graph-row-horizontal-bar'>
              <GraphTitle title='Topics created per user role'
                          customClass='graph-title-horizontal-bar'
                          tooltipMessage='The % of topics created in the selected time period broken down by the primary role of the author of the topic.' />
                  {topicsCreatedPerUserRoleGraphLoading ? <Loader />
                  : topicsPerUserRole && <HorizontalBar redraw={shouldChartRedraw} data={this.getTopicsCreatedDataSet(topicsPerUserRole)} options={this.getTopicsCreatedHorizonalBarOptions()} />}
            </div>
          </div>
        </div>
        <Tabs titles={['Popular topics', 'Popular categories']}>
          <PopularTopicsTable endDate={endDate}
                              startDate={startDate}
                              selectedRoles={selectedRoles}
                              selectedContentTypes={selectedContentTypes}
                              selectedCategories={selectedCategories}
                              fetchData={this.fetchData} />
          <PopularCategoriesTable endDate={endDate}
                                  startDate={startDate}
                                  selectedRoles={selectedRoles}
                                  selectedContentTypes={selectedContentTypes}
                                  selectedCategories={selectedCategories}
                                  fetchData={this.fetchData} />
        </Tabs>
        <DashboardTimezoneDisclaimer />
      </div>)
  }
}

export default ContentDashboard
